<template>
  <div class="container py-5">
    <div class="row justify-content-center">
      <div class="col-md-6 col-lg-5">
        <div class="card shadow-lg border-0">
          <div class="card-body p-5">
            <!-- Logo/Header -->
            <div class="text-center mb-4">
              <h2 class="fw-bold heading-accent">Welcome Back</h2>
              <p class="text-muted">Sign in to your account</p>
            </div>

            <!-- Login Form -->
            <form @submit.prevent="handleLogin">
              <!-- Email -->
              <div class="mb-4">
                <label class="form-label fw-semibold">Email Address</label>
                <div class="input-group">
                  <span class="input-group-text"><i class="bi bi-envelope"></i></span>
                  <input v-model="form.email" type="email" class="form-control" placeholder="you@example.com" required>
                </div>
              </div>

              <!-- Password -->
              <div class="mb-4">
                <div class="d-flex justify-content-between">
                  <label class="form-label fw-semibold">Password</label>
                  <a href="#" class="text-decoration-none link-accent small" @click="$router.push('/forgot-password')">Forgot Password?</a>
                </div>
                <div class="input-group">
                  <span class="input-group-text"><i class="bi bi-lock"></i></span>
                  <input v-model="form.password" :type="showPassword ? 'text' : 'password'" class="form-control" placeholder="Enter your password" required>
                  <button @click="showPassword = !showPassword" class="btn btn-outline-secondary" type="button">
                    <i :class="showPassword ? 'bi bi-eye-slash' : 'bi bi-eye'"></i>
                  </button>
                </div>
              </div>

              <!-- Remember Me -->
              <div class="mb-4 form-check">
                <input v-model="rememberMe" type="checkbox" class="form-check-input" id="remember">
                <label class="form-check-label" for="remember">Remember me</label>
              </div>

              <!-- Error Message -->
              <div v-if="error" class="alert alert-danger d-flex align-items-center">
                <i class="bi bi-exclamation-circle me-2"></i>
                <div>{{ error }}</div>
              </div>

              <!-- Submit Button -->
              <button type="submit" class="btn btn-primary btn-lg w-100 mb-4" :disabled="loading">
                <span v-if="loading" class="spinner-border spinner-border-sm me-2"></span>
                {{ loading ? 'Signing in...' : 'Sign In' }}
              </button>

              <!-- Register Link -->
              <div class="text-center">
                <p class="text-muted">
                  Don't have an account?
                  <a href="#" @click.prevent="goToRegister" class="text-decoration-none fw-semibold link-accent">Sign up</a>
                </p>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>

    <!-- Verification Modal -->
    <div v-if="showVerification" class="modal fade show d-block modal-overlay">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content text-center p-4">
          <div class="verification-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
              <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.362 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.338 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"></path>
            </svg>
          </div>
          <h3 class="mt-3">Account Not Verified</h3>
          <p class="text-muted">We've sent a verification code to your phone number</p>
          <p class="phone-number">{{ maskedPhone }}</p>

          <div class="code-inputs mt-3">
            <input
              v-for="(digit, index) in 6"
              :key="index"
              type="text"
              maxlength="1"
              class="code-input"
              v-model="verificationCode[index]"
              @input="handleCodeInput(index, $event)"
              @keyup="handleCodeKeyup(index, $event)"
              ref="codeInputs"
            />
          </div>

          <div class="mt-3">
            <button
              class="btn btn-primary w-100"
              @click="verifyAccount"
              :disabled="verifying || !isCodeComplete"
            >
              <span v-if="verifying" class="spinner-border spinner-border-sm me-2"></span>
              {{ verifying ? "Verifying..." : "Verify Account" }}
            </button>
          </div>

          <div class="mt-3">
            <button
              class="btn btn-link"
              @click="resendCode"
              :disabled="resendTimer > 0"
            >
              {{ resendTimer > 0 ? `Resend code in ${resendTimer}s` : "Resend Code" }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'LoginPage',
  data() {
    return {
      form: {
        email: '',
        password: ''
      },
      rememberMe: false,
      error: '',
      loading: false,
      showPassword: false,
      showVerification: false,
      verifying: false,
      maskedPhone: '',
      userId: null,
      verificationCode: ['', '', '', '', '', ''],
      resendTimer: 0,
      API_BASE_URL:'https://golang.roberms.com/api' //'https://golang.roberms.com/api'
    }
  },
  computed: {
    isCodeComplete() {
      return this.verificationCode.every(digit => digit && digit.length === 1);
    },
    fullVerificationCode() {
      return this.verificationCode.join("");
    }
  },
  methods: {
    handleCodeInput(index, event) {
      const value = event.target.value;
      if (value && /^\d$/.test(value)) {
        this.verificationCode[index] = value;
        if (index < 5) {
          this.$refs.codeInputs[index + 1].focus();
        }
      } else if (value === "") {
        this.verificationCode[index] = "";
      } else {
        this.verificationCode[index] = "";
      }
    },

    handleCodeKeyup(index, event) {
      if (event.key === 'Backspace' && index > 0 && !this.verificationCode[index]) {
        this.$refs.codeInputs[index - 1].focus();
      }
    },

    async handleLogin() {
      this.loading = true
      this.error = ''

      try {
        const response = await fetch(`${this.API_BASE_URL}/login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(this.form)
        })

        const data = await response.json()

        // Check if response is not ok
        if (!response.ok) {
          // Check if the error is due to unverified account
          if (response.status === 403 && data.requires_verification) {
            // Show verification modal
            this.maskedPhone = data.phone_number
            this.userId = data.user_id
            this.showVerification = true
            this.startResendTimer()
            this.loading = false
            return
          }
          throw new Error(data.error || 'Login failed')
        }

        // Store token only for verified users
        if (data.data && data.data.token) {
          localStorage.setItem('token', data.data.token)
          localStorage.setItem('user', JSON.stringify(data.data.user))
          localStorage.setItem('userId', data.data.user.id)
          if (this.rememberMe) {
            localStorage.setItem('user_email', this.form.email)
          }
        }

        // Redirect to dashboard
        this.$router.push('/dashboard')

      } catch (err) {
        this.error = err.message || 'Invalid email or password'
      } finally {
        this.loading = false
      }
    },

    async verifyAccount() {
      if (!this.isCodeComplete) return;

      this.verifying = true;
      this.error = '';

      try {
        const response = await fetch(`${this.API_BASE_URL}/verify/account`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            verification_code: this.fullVerificationCode
          })
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || 'Verification failed');
        }

        // Close verification modal
        this.showVerification = false;

        // Auto login after verification
        if (data.data && data.data.token) {
          localStorage.setItem('token', data.data.token)
          localStorage.setItem('user', JSON.stringify(data.data.user))
          if (this.rememberMe) {
            localStorage.setItem('user_email', this.form.email)
          }
        }

        // Redirect to dashboard
        this.$router.push('/dashboard')

      } catch (err) {
        this.error = err.message;
        alert('Verification failed: ' + err.message);
      } finally {
        this.verifying = false;
      }
    },

    async resendCode() {
      if (this.resendTimer > 0) return;

      try {
        const response = await fetch(`${this.API_BASE_URL}/resend-verification`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            user_id: this.userId
          })
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || 'Failed to resend code');
        }

        this.verificationCode = ['', '', '', '', '', ''];
        if (this.$refs.codeInputs && this.$refs.codeInputs[0]) {
          this.$refs.codeInputs[0].focus();
        }

        this.startResendTimer();
        alert('New verification code sent!');

      } catch (err) {
        alert('Failed to resend code: ' + err.message);
      }
    },

    startResendTimer() {
      this.resendTimer = 60;
      const interval = setInterval(() => {
        if (this.resendTimer > 0) {
          this.resendTimer--;
        } else {
          clearInterval(interval);
        }
      }, 1000);
    },

    goToRegister() {
      this.$router.push('/register')
    }
  },
  mounted() {
    // Auto-fill email if remembered
    const savedEmail = localStorage.getItem('user_email')
    if (savedEmail) {
      this.form.email = savedEmail
      this.rememberMe = true
    }
  }
}
</script>

<style scoped>

.container {
  min-height: 100vh;
  background: var(--paper, var(--dash-bg));
}

.card {
  border-radius: var(--radius-lg, 24px);
  border: none;
  background: var(--paper-dim, var(--dash-bg-alt));
  box-shadow: var(--dash-shadow);
}

.heading-accent {
  font-family: var(--font-display, inherit);
  color: var(--ink, var(--dash-ink));
}

.btn-primary {
  background: var(--dash-primary);
  border: none;
  padding: 12px;
  font-family: var(--font-body, inherit);
  transition: all 0.3s var(--ease, ease);
}

.btn-primary:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: var(--dash-shadow);
}

.link-accent {
  color: var(--azure, var(--dash-primary));
}

.link-accent:hover {
  color: var(--azure-soft, var(--dash-primary-hover));
}

.input-group:focus-within {
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.12);
  border-radius: var(--dash-radius);
}

.form-control,
.form-label,
.form-check-label,
.text-muted,
p, h2, h3 {
  font-family: var(--font-body, inherit);
}

.form-control:focus {
  border-color: var(--azure, var(--dash-primary));
  box-shadow: none;
}

.alert {
  border-radius: var(--dash-radius-xs);
  border: none;
}

.alert-danger {
  background: color-mix(in srgb, var(--rust, var(--dash-danger)) 12%, white);
  color: var(--rust, var(--dash-danger));
}

/* Verification Modal Styles */
.modal-overlay {
  background: rgba(16, 27, 45, 0.6);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1050;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-dialog {
  max-width: 500px;
  width: 90%;
  margin: auto;
}

.modal-content {
  border-radius: var(--dash-radius-lg);
  background: var(--paper-dim, var(--dash-bg-alt));
  font-family: var(--font-body, inherit);
}

.verification-icon {
  width: 80px;
  height: 80px;
  background: var(--dash-primary-soft);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
  color: var(--dash-primary-ink);
}

.verification-icon svg {
  color: var(--dash-primary-ink);
}

.phone-number {
  font-size: 18px;
  font-weight: 600;
  color: var(--azure, var(--dash-primary));
  margin: 10px 0 5px;
}

.code-inputs {
  display: flex;
  gap: 12px;
  justify-content: center;
}

.code-input {
  width: 50px;
  height: 60px;
  text-align: center;
  font-size: 24px;
  font-weight: 600;
  font-family: var(--font-mono, inherit);
  border: 2px solid var(--line, var(--dash-line));
  border-radius: var(--dash-radius);
  background: var(--paper, var(--dash-bg));
  color: var(--ink, var(--dash-ink));
  transition: all 0.3s var(--ease, ease);
}

.code-input:focus {
  outline: none;
  border-color: var(--azure, var(--dash-primary));
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.12);
}

.btn-link {
  text-decoration: none;
  color: var(--azure, var(--dash-primary));
  font-family: var(--font-body, inherit);
}

.btn-link:hover {
  text-decoration: underline;
}

.btn-link:disabled {
  color: var(--slate-soft, var(--dash-faint));
  cursor: not-allowed;
}

@media (max-width: 640px) {
  .code-input {
    width: 40px;
    height: 50px;
    font-size: 20px;
  }

  .code-inputs {
    gap: 8px;
  }
}
</style>